import React from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { NO_INFORMATION_AVAILABLE } from '../../constants';

/**
 * CardV2 Component
 *
 * A reusable, clickable card component that displays an icon, title, description,
 * main metric, and optional details. It navigates to a specified URL on click and
 * supports additional custom styling via the `className` prop.
 *
 * Props:
 * - icon: React component to render as the card's icon.
 * - title: Card title text.
 * - copy: Descriptive text below the title.
 * - url: URL for navigation when the card is clicked.
 * - mainMetric: Prominent metric value.
 * - details: Optional additional details (string or React node).
 * - className: Optional custom class names for additional styling.
 */
const CardV2 = ({
  icon: IconComponent,
  title,
  copy,
  url,
  mainMetric = '',
  details,
  className = '',
  cardError = false,
}) => {
  return (
    <Link
      to={url}
      className={`group relative bg-white text-left rounded-3xl p-8 shadow-sm flex flex-col gap-3 w-full min-w-[300px] max-w-[350px] min-h-[280px] transform transition-all duration-200 ease-out
      hover:scale-105 hover:shadow-lg ${className}`}
    >
      <div className="flex items-center gap-2">
        {IconComponent && (
          <div className="text-black w-6 h-6">
            <IconComponent className="w-full h-full" />
          </div>
        )}
        <h2 className="text-black font-bold text-2xl">{title}</h2>
      </div>
      {cardError ? (
        <p className="text-sm font-medium text-gray-500 mt-6">{NO_INFORMATION_AVAILABLE}</p>
      ) : (
        <>
          <p className="text-sm font-medium text-gray-500 mt-6">{copy}</p>
          <p className="text-3xl font-semibold text-gray-500">{mainMetric}</p>
          {details && <div className="text-sm text-gray-500">{details}</div>}
        </>
      )}
    </Link>
  );
};

CardV2.propTypes = {
  icon: PropTypes.elementType,
  title: PropTypes.string.isRequired,
  copy: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  mainMetric: PropTypes.string,
  details: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  className: PropTypes.string,
  cardError: PropTypes.bool,
};

export default CardV2;
